import React, { useEffect, useState } from 'react';
import { Autocomplete, Checkbox, MenuItem, TextField, Select, InputLabel, FormControl, CircularProgress, Button, Grid2, Alert, Snackbar, Typography, Box } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../services/authentication/authContext';
import { useTranslation } from 'react-i18next';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import { taskServices } from '../../services/taskServices';
import { userServices } from '../../services/userServices';
import '../../css/Form.css';
import { useSelector } from 'react-redux';
import CalculateBudgetHours from '../job/CalculateBudgetHours';
import PageHeader from '../ui/PageHeader';
import ConfirmationDialog from '../common/ConfirmationDialog';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const TaskForm = () => {
  const [jobData, setJobData] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState('');
  const [taskTitle, setTaskTitle] = useState([]);
  const [taskDescription, setTaskDescription] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [taskId, setTaskId] = useState('');
  const [jobName, setJobName] = useState('');
  const [jobInput, setJobInput] = useState('');
  const [jobLoading, setJobLoading] = useState(false);
  const [assignedUserId, setAssignedUserId] = useState('');
  const [startTime, setStartTime] = useState('');
  const [userName, setUserName] = useState('');
  const [taskTitleData, setTaskTitleData] = useState([]);
  const task = location.state?.taskData
  const id = taskId;
  const navigationUrl = "/home/tasks";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userData, setUserData] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [jobType, setJobType] = useState('');
  const [status, setStatus] = useState('');
  const [currentStatusValue, setCurrentStatusValue] = useState('');
  const [budgetHours, setBudgetHours] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');
  const [existingDataBudgetHours, setExistingDataBudgetHours] = useState(null);
  const { data: jobStatusData } = useSelector(state => state.jobStatus);
  const [filteredStatuses, setFilteredStatuses] = useState([]);
  const [budgetUpdatedAt, setBudgetUpdatedAt] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [budgetUpdatedByUser, setBudgetUpdatedByUser] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [showBackConfirmation, setShowBackConfirmation] = useState(false);

  useEffect(() => {
    if (task) {
      setTaskId(task.id);
    }
  }, [task]);

  const fetchUserData = async () => {
    setLoading(true);
    try {
      userServices.getAllUsersWhichHasNoAdminRole()
        .then((response) => {
          setUserData(response);
        })
        .catch((error) => {
          setLoading(false);
        });
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function for BudgetHours Formatting
  const formatBudgetHours = (value) => {
    if (value === null || value === undefined || value === '') return '';

    const num = parseFloat(value);
    if (Number.isNaN(num)) return '';
    const [hrs, mins] = num.toFixed(2).split('.');
    const paddedHrs = hrs.padStart(2, '0');

    return `${paddedHrs}.${mins}`;
  };

  useEffect(() => {
    if (accessToken) {
      if (taskId) {
        setLoading(true);
        taskServices.getTaskDataById(taskId)
          .then((response) => {
            setTaskTitle(response.titles);
            setTaskDescription(response.description);
            setJobName(response.job.name);
            setAssignedUserId(response.assignedUser.id);
            setUserName(response.assignedUser.firstName + " " + response.assignedUser.lastName);
            setSelectedJobId(response.job.id);
            setStartTime(response.startTime);
            setBudgetHours(formatBudgetHours(response.budgetHours));
            setJobType(response.job.type);

            const rawJson = response.job.budgetHoursJson;
            const parsedJson = typeof rawJson === 'string'
              ? JSON.parse(rawJson)
              : rawJson;
            setExistingDataBudgetHours(
              Array.isArray(parsedJson) && parsedJson.length === 0 ? null : parsedJson
            );

            const budgetUpdatedByUserFirstName = response?.budgetUpdatedBy ? response?.budgetUpdatedBy?.firstName : '';
            const budgetUpdatedByUserLastName = response?.budgetUpdatedBy ? response?.budgetUpdatedBy?.lastName : '';
            const budgetUpdatedByUser = `${budgetUpdatedByUserFirstName} ${budgetUpdatedByUserLastName}`;
            setBudgetUpdatedByUser(budgetUpdatedByUser);

            if (response?.budgetUpdatedAt != null) {
              const localDate = new Date(response?.budgetUpdatedAt);
              const dateOnly = String(localDate.getDate()).padStart(2, '0') +
                '/' + String(localDate.getMonth() + 1).padStart(2, '0') +
                '/' + localDate.getFullYear();
              setBudgetUpdatedAt(dateOnly);
            } else {
              setBudgetUpdatedAt('');
            }
            setLoading(false);
          })
          .catch((error) => {
            setError(error.message);
            setTimeout(() => {
              setError(null);
            }, 5000);
            setLoading(false);
          });

      } else {
        const fetchInitialData = async () => {
          setLoading(true);
          setJobLoading(true);
          try {
            const [jobs, titles, users] = await Promise.all([
              taskServices.getCurrentJobsAssignedByUser(0, 0, '', '', false, '', ''),
              taskServices.getTaskTitles(),
              userServices.getAllUsersWhichHasNoAdminRole()
            ]);

            const normalizedData = (jobs || []).map(job => ({
              id: job.jobId,
              name: job.jobName,
              type: job.type,
              status: job.status,
              budgetHours: job.budgetHours,
              budgetHoursJson: typeof job.budgetHoursJson === 'string'
                ? JSON.parse(job.budgetHoursJson)
                : job.budgetHoursJson,
              assignedUserId: job.userId,
              budgetUpdatedBy: job.budgetUpdatedByFirstName
                ? {
                  id: job.budgetUpdatedByUserId,
                  firstName: job.budgetUpdatedByFirstName,
                  lastName: job.budgetUpdatedByLastName
                }
                : null,
              budgetUpdatedAt: job.budgetUpdatedAt
            }));
            setJobData(normalizedData);

            const formattedTaskTitles = Object.entries(titles || {}).map(([value, label]) => ({
              value: parseInt(value),
              label: label
            }));
            setTaskTitleData(formattedTaskTitles);
            setUserData(users || []);

          } catch (error) {
            console.error("Error fetching initial data:", error);
            setError('Error fetching data: ' + error.message);
            setTimeout(() => setError(null), 5000);
          } finally {
            setLoading(false);
            setJobLoading(false);
          }
        };

        fetchInitialData();
      }
    }
  }, [taskId, accessToken]);

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  // Save updated data when modal is closed
  const handleSaveDataJobPlan = (updatedData) => {
    setExistingDataBudgetHours(updatedData.categories);
    setBudgetHours(updatedData ? updatedData.totalTime.replace(":", ".") : '');
    handleCloseModal(); // Close the modal after saving
  };

  const removeDetailsBudgetHours = (data) => {
    if (!data) {
      return null;
    }
    return data.map(category => ({
      ...category,
      data: category.data ? category.data.map(({ details, ...rest }) => rest) : [] // Ensure `data` is an array before calling map
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const budgetHoursJson = removeDetailsBudgetHours(existingDataBudgetHours);

    setIsSubmitting(true);
    const taskTitles = Array.isArray(taskTitle) ? taskTitle.map(item => item.label) : [taskTitle];

    const formData = {
      titles: taskTitles,
      description: taskDescription,
      jobId: selectedJobId,
      assignedUserId,
      budgetHours: budgetHours,
      budgetHoursJson: budgetHoursJson
    };

    const formUpdate = {
      id,
      titles: taskTitles,
      description: taskDescription,
      jobId: selectedJobId,
      assignedUserId,
      budgetHours: budgetHours,
      budgetHoursJson: budgetHoursJson
    };

    if (accessToken) {
      if (taskId) {

        taskServices.updateTaskData(formUpdate)
          .then((updateTask) => {
            localStorage.setItem("successMessage", "Task Updated successfully!");
            navigate(navigationUrl);
          })
          .catch((error) => {
            if (error.response?.status === 400) {
              setSnackbarMessage(error.response.data);
            } else {
              setSnackbarMessage(error.message);
            }
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
          });

      } else {
        taskServices.postTaskData(formData)
          .then((newTask) => {
            localStorage.setItem("successMessage", "Task added successfully!");
            navigate(navigationUrl);
          })
          .catch((error) => {
            if (error.response?.status === 400) {
              setSnackbarMessage(error.response.data);
            } else {
              setSnackbarMessage(error.message);
            }
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
          });
      }
    }
  };

  const handleCancel = () => {
    handleBack();
  };

  const handleBack = () => {
    if (isDirty) {
      setShowBackConfirmation(true);
    } else {
      navigate(navigationUrl);
    }
  };

  const handleConfirmBack = () => {
    setShowBackConfirmation(false);
    navigate(navigationUrl);
  };

  const handleJobChange = (e) => {
    const jobId = e.target.value;
    setSelectedJobId(jobId);

    const selectedJob = jobData.find(job => job.id === jobId);

    setStatus(selectedJob?.status || '');
    setCurrentStatusValue(selectedJob?.status || '');
    setAssignedUserId(selectedJob?.assignedUserId || '');
    setExistingDataBudgetHours(selectedJob?.budgetHoursJson || null);
    setBudgetHours(selectedJob?.budgetHours || '');
    setJobType(selectedJob?.type || '');

    const budgetUpdatedByUser = selectedJob?.budgetUpdatedBy
      ? `${selectedJob.budgetUpdatedBy.firstName} ${selectedJob.budgetUpdatedBy.lastName}`
      : '';
    setBudgetUpdatedByUser(budgetUpdatedByUser);

    if (selectedJob?.budgetUpdatedAt != null) {
      const localDate = new Date(selectedJob?.budgetUpdatedAt);
      const dateOnly = String(localDate.getDate()).padStart(2, '0') +
        '/' + String(localDate.getMonth() + 1).padStart(2, '0') +
        '/' + localDate.getFullYear();
      setBudgetUpdatedAt(dateOnly);
    } else {
      setBudgetUpdatedAt('');
    }
  };

  const handleUserChange = (e) => {
    setAssignedUserId(e.target.value);
  };

  const handleTaskTitleChange = (event, value, reason) => {
    setTaskTitle(value || []);
    if (!loading && (reason === 'input' || reason === 'selectOption' || reason === 'removeOption' || reason === 'clear')) {
      setIsDirty(true);
    }
  };

  const jobFilter = (options, { inputValue }) => {
    return options.filter((option) =>
      option?.name?.toLowerCase().includes(inputValue.toLowerCase())
    );
  };

  return (
    <Box className="main-form-container">
      <PageHeader
        title={taskId ? "Edit Task" : "Add Task"}
        left={
          <IconButton onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
        }
      />
      <form onSubmit={handleSubmit} className="form" style={{ marginTop: '1.25rem' }} onChange={() => { if (!loading) setIsDirty(true); }}>
        <Grid2 container spacing={3}>
          {/* Job Selection */}
          <Grid2 size={{ xs: 12, md: 6 }}>
            {taskId ? (
              <TextField
                type="text"
                label="Job"
                variant="outlined"
                fullWidth
                required
                value={jobName}
                margin="normal"
                disabled
              />
            ) : (
              <Autocomplete
                options={jobData}
                getOptionLabel={(option) => option.name}
                value={jobData.find((job) => job.id === selectedJobId) || null}
                loading={jobLoading}
                inputValue={jobInput}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input') {
                    setJobInput(newInputValue + '  ');
                    setTimeout(() => {
                      setJobInput(newInputValue);
                    }, 0);
                  } else {
                    setJobInput(newInputValue);
                  }
                }}
                filterOptions={jobFilter}
                onChange={(event, newValue) => {
                  if (newValue) {
                    handleJobChange({ target: { value: newValue.id } });
                    setJobInput(newValue.name);
                  } else {
                    setSelectedJobId('');
                    setJobInput('');
                  }
                  if (!loading) {
                    setIsDirty(true);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Job"
                    margin="normal"
                    fullWidth
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.name}
                  </li>
                )}
                noOptionsText={t('No options')}
                disableClearable
              />
            )}

            {selectedJobId && (
              <Grid2 size={{ xs: 12 }}>
                <Typography variant="body1">
                  Job Type: {jobType}
                </Typography>
              </Grid2>
            )}
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            {taskId ? (
              <Autocomplete
                value={taskTitle}
                onInputChange={handleTaskTitleChange}
                options={taskTitleData.map((option) => option.label)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('task.taskTitle')}
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    required
                  />
                )}
                disableClearable
                freeSolo
                loading={loading}
                noOptionsText={t('noOptions')}
                isOptionEqualToValue={(option, value) => option === value}
                disabled
              />
            ) : (
              <Autocomplete
                multiple
                value={taskTitle}
                onChange={handleTaskTitleChange}
                options={taskTitleData}
                getOptionLabel={(option) => option.label}
                disableCloseOnSelect
                renderOption={(props, option, { selected }) => {
                  const { key, ...restProps } = props;

                  return (
                    <li key={key} {...restProps}>
                      <Checkbox style={{ marginRight: 8 }} checked={selected} />
                      {option.label}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField {...params} label={t('task.taskTitle')} variant="outlined" fullWidth margin="normal" />
                )}
                loading={loading}
                noOptionsText={t('noOptions')}
                isOptionEqualToValue={(option, value) => option.value === value.value}
              />)}
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            {startTime ? (
              <TextField
                type="text"
                label="User"
                variant="outlined"
                fullWidth
                required
                value={userName}
                margin="normal"
                disabled
              />
            ) : (
              <FormControl fullWidth margin="normal">
                <InputLabel>User</InputLabel>
                <Select
                  value={(Array.isArray(userData) && userData.some((u) => String(u.id) === String(assignedUserId))) ? assignedUserId : ''}
                  onChange={handleUserChange}
                  label="User"
                  required
                >
                  {loading ? (
                    <MenuItem value="" disabled>
                      <CircularProgress size={24} sx={{ display: 'flex', justifyContent: 'center' }} />
                    </MenuItem>
                  ) : userData.length > 0 ? (
                    userData.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.firstName} {user.middleName} {user.lastName}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="" disabled>
                      <span sx={{ color: 'text.secondary' }}>No users available</span>
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            )}
          </Grid2>

          <Grid2 container spacing={2} alignItems="center" justifyContent="center">
            {/* Budget Hours TextField */}
            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                type="number"
                label={t('job.budget_hours')}
                variant="outlined"
                fullWidth
                value={budgetHours}
                onChange={(e) => setBudgetHours(e.target.value)}
                margin="normal"
                disabled
              />
            </Grid2>

            {/* Budget Hours Button */}
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Button
                variant="contained"
                onClick={handleOpenModal}
                className='form-button-budget'
                disabled={!selectedJobId || status === 'Completed' || status === 'Second Phase'}
              >
                Job Plan
              </Button>

              <CalculateBudgetHours
                open={openModal}
                onClose={handleCloseModal}
                existingData={existingDataBudgetHours}
                onSave={handleSaveDataJobPlan}
                jobType={jobType}
                jobId={selectedJobId}
                budgetUpdatedByUser={budgetUpdatedByUser}
                budgetUpdatedAt={budgetUpdatedAt}
              />
            </Grid2>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            {taskId && <TextField
              label={t('task.desc')}
              variant="outlined"
              fullWidth
              size="small"
              multiline
              rows={4}
              value={taskDescription}
              onChange={(e) => setTaskDescription(e.target.value)}
              placeholder="Enter Task Description"
              required
            />}
          </Grid2>

          <Grid2 size={{ xs: 12 }} className="form-actions" sx={{ flexDirection: { xs: 'row !important', sm: 'row' }, gap: { xs: 2, sm: 0 }, '& > button': { flex: { xs: 1, sm: 'none' }, margin: { xs: '0 !important', sm: '' } } }}>
            {taskId ?
              (<Button type="submit" variant="contained" className="form-button"
                startIcon={<EditIcon />}>
                {t('task.update')}
              </Button>
              ) : (
                <Button type="submit" variant="contained" className="form-button"
                  startIcon={<SaveIcon />}>
                  {t('task.save')}
                </Button>
              )}
            <Button type="button" variant="contained" color="inherit" onClick={handleCancel} className="form-button-reset"
              startIcon={<CancelIcon />}>
              {t('task.cancel')}
            </Button>
          </Grid2>
        </Grid2>
      </form>

      <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ whiteSpace: 'pre-line' }}>
          {Array.isArray(snackbarMessage) ? (
            snackbarMessage.map((msg, index) => (
              <div key={index}>{msg}</div>
            ))
          ) : (
            snackbarMessage
          )}
        </Alert>
      </Snackbar>

      <ConfirmationDialog
        open={showBackConfirmation}
        onClose={() => setShowBackConfirmation(false)}
        onConfirm={handleConfirmBack}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to leave this page?"
        confirmLabel="Leave Page"
        confirmColor="error"
      />
    </Box >
  );
};

export default TaskForm;
